/**
 * Emit hosts for the `medical-director` analytics group — 4 functions
 * lifted out of `analyticsScribeModel`'s effects so a call site can emit
 * without holding a Rematch `dispatch`. Every payload is the effect's own.
 */
import { segmentClient } from '@/lib/analytics/legacy-sender-context';
import { track } from '@/lib/analytics/track';
import { trackLegacyEvent } from '@/lib/analytics/track-legacy-event';
import { TrackingEventName } from '@/models/analytics/eventTypes';
import { SegmentActionName, SegmentObjectName } from '@/models/analytics/segment-names';
import { type Maybe } from '@/types';
import { type EHRProvider } from '@heidi/schema/ml-scribe';
import posthog from 'posthog-js';
import { trackIntegrationDataFailed } from './integrations';

export function trackMedicalDirectorGetDemographicsFailed(error: string) {
  trackLegacyEvent({
    name: SegmentObjectName.MedicalDirector,
    props: {
      action: SegmentActionName.MedicalDirectorGetDemographicsFailed,
      error: error,
    },
  });
}

export function trackMedicalDirectorGetPatientSessionsFailed(error: string) {
  trackLegacyEvent({
    name: SegmentObjectName.MedicalDirector,
    props: {
      action: SegmentActionName.MedicalDirectorGetPatientSessionsFailed,
      error: error,
    },
  });

  // V2
  trackIntegrationDataFailed({
    integration_type: 'medical_director',
    ehr_provider: 'medical_director',
    data_type: 'patient_sessions',
    error_message: error,
  });
}

export function trackMedicalDirectorSyncSessionsFailed(error: string) {
  trackLegacyEvent({
    name: SegmentObjectName.MedicalDirector,
    props: {
      action: SegmentActionName.MedicalDirectorSyncSessionsFailed,
      error: error,
    },
  });

  // V2
  track({
    options: { amplitude: false },
    event_name: TrackingEventName.IntegrationFailed,
    props: {
      integration_type: 'medical_director',
      failure_action: 'sync_sessions_failed',
      error_message: error,
    },
  });
}

export function userHasMedicalDirector(input: {
  authID: string;
  userTraits: {
    ehr_provider: Maybe<EHRProvider>;
    from?: Maybe<string>;
  };
}) {
  const { authID, userTraits } = input;
  void segmentClient.identify(authID, userTraits);
  posthog.identify(authID, {
    ehr_provider: userTraits.ehr_provider,
    from: userTraits.from,
  });
}
